import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";
import { createOrder } from "../lib/api";
import { useTransportQuote } from "../hooks/useTransportQuote";
import { Loader2, Check, FileText, Info, Truck, RefreshCw } from "lucide-react";

const COUNTRIES = [
  { code: "NL", name: "Nederland", vat: 0.21, reverse: false },
  { code: "BE", name: "België", vat: 0.0, reverse: true },
  { code: "DE", name: "Duitsland", vat: 0.0, reverse: true },
  { code: "FR", name: "Frankrijk", vat: 0.0, reverse: true },
  { code: "LU", name: "Luxemburg", vat: 0.0, reverse: true },
  { code: "AT", name: "Oostenrijk", vat: 0.0, reverse: true },
  { code: "CH", name: "Zwitserland", vat: 0.0, reverse: true },
  { code: "GB", name: "Verenigd Koninkrijk", vat: 0.0, reverse: true },
  { code: "DK", name: "Denemarken", vat: 0.0, reverse: true },
  { code: "ES", name: "Spanje", vat: 0.0, reverse: true },
  { code: "IT", name: "Italië", vat: 0.0, reverse: true },
  { code: "PL", name: "Polen", vat: 0.0, reverse: true },
  { code: "SE", name: "Zweden", vat: 0.0, reverse: true },
  { code: "OTHER", name: "Anders / export buiten EU", vat: 0.0, reverse: true },
];

export default function CheckoutPage() {
  const { t } = useTranslation();
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [shipSameAsOrdered, setShipSameAsOrdered] = useState(true);
  const [billSameAsShip, setBillSameAsShip] = useState(true);
  const [form, setForm] = useState({
    // Besteld door (Ordered by — account / buyer contact)
    customer_name: "", customer_email: "", customer_phone: "",
    company: "", vat_number: "",
    // Leveren aan (Ship to)
    shipping_contact_name: "", shipping_company: "",
    shipping_address: "", shipping_city: "", shipping_postal: "",
    shipping_country_code: "NL",
    // Factuur voor (Bill to)
    billing_contact_name: "", billing_company: "", billing_vat_number: "",
    billing_address: "", billing_city: "", billing_postal: "",
    billing_country_code: "NL",
    notes: "",
  });

  const set = (k, v) => setForm({ ...form, [k]: v });

  const country = useMemo(
    () => COUNTRIES.find((c) => c.code === form.shipping_country_code) || COUNTRIES[0],
    [form.shipping_country_code],
  );
  const vat = subtotal * country.vat;
  const fallbackShipping = subtotal > 500 ? 0 : 19.95;

  // Parse "Straat huisnummer" into street + number for the TFF rate query.
  // Best-effort: "Domkloster 4a" → {street: "Domkloster", number: "4a"}.
  const parsedAddr = useMemo(() => {
    const raw = (form.shipping_address || "").trim();
    if (!raw) return { street: "", number: "" };
    const m = raw.match(/^(.+?)\s+(\d+[a-zA-Z]?(?:-\d+)?)$/);
    if (m) return { street: m[1].trim(), number: m[2].trim() };
    return { street: raw, number: "" };
  }, [form.shipping_address]);

  const transportDelivery = useMemo(() => ({
    country: form.shipping_country_code === "OTHER" ? "" : form.shipping_country_code,
    postal: form.shipping_postal,
    city: form.shipping_city,
    street: parsedAddr.street,
    number: parsedAddr.number,
  }), [form.shipping_country_code, form.shipping_postal, form.shipping_city, parsedAddr]);

  const tq = useTransportQuote(
    transportDelivery,
    items.map((i) => ({ sku: i.sku, quantity: i.quantity, meta: i.meta })),
  );

  const shipping = tq.selected ? tq.selected.price : fallbackShipping;
  const total = subtotal + vat + shipping;

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setSubmitting(true);
    try {
      // Resolve "same as" toggles → fill empty fields from the prior pillar.
      const shipCountry = COUNTRIES.find((c) => c.code === form.shipping_country_code) || COUNTRIES[0];
      const billCountryCode = billSameAsShip ? form.shipping_country_code : form.billing_country_code;
      const billCountry = COUNTRIES.find((c) => c.code === billCountryCode) || shipCountry;
      const payload = {
        // Besteld door
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        company: form.company,
        vat_number: form.vat_number,
        // Leveren aan
        shipping_address: form.shipping_address,
        shipping_city: form.shipping_city,
        shipping_postal: form.shipping_postal,
        shipping_country: shipCountry.name,
        shipping_country_code: shipCountry.code === "OTHER" ? "" : shipCountry.code,
        shipping_contact_name: shipSameAsOrdered ? (form.customer_name || null) : (form.shipping_contact_name || null),
        shipping_company: shipSameAsOrdered ? (form.company || null) : (form.shipping_company || null),
        // Factuur voor — when same-as toggle is on, set fields to null so backend
        // treats it as "billing = shipping". When different, send the explicit values.
        billing_contact_name: billSameAsShip ? null : (form.billing_contact_name || null),
        billing_company: billSameAsShip ? null : (form.billing_company || null),
        billing_vat_number: billSameAsShip ? null : (form.billing_vat_number || null),
        billing_address: billSameAsShip ? null : form.billing_address,
        billing_city: billSameAsShip ? null : form.billing_city,
        billing_postal: billSameAsShip ? null : form.billing_postal,
        billing_country: billSameAsShip ? null : billCountry.name,
        billing_country_code: billSameAsShip ? null : (billCountry.code === "OTHER" ? "" : billCountry.code),
        notes: form.notes,
        payment_method: "factuur",
        transport_tier: tq.selected ? `${tq.selected.carrier} — ${tq.selected.label}` : null,
        transport_price: tq.selected ? tq.selected.price : null,
        items: items.map((i) => ({
          product_id: i.product_id, sku: i.sku, name: i.name, quantity: i.quantity,
          unit_price: i.unit_price, config: i.config, image: i.image,
        })),
      };
      const order = await createOrder(payload);
      clear();
      navigate(`/order/${order.order_number}`);
    } catch (e) {
      console.error(e);
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : t("checkout.submit_error");
      alert(msg);
    } finally { setSubmitting(false); }
  };

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-[1200px] px-5 py-20 text-center">
        <h2 className="font-display text-3xl text-zinc-900">{t("checkout.empty_cart_title")}</h2>
        <button type="button" onClick={() => navigate("/products")} className="mt-6 rounded-md bg-nibro-sage px-6 py-3 text-sm font-bold text-white">
          {t("cart.browse_products")}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="checkout-page">
      <div className="mx-auto max-w-[1400px] px-5 py-10 lg:px-10">
        <div className="label-uppercase mb-3">{t("checkout.title")}</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-zinc-900 sm:text-5xl">{t("checkout.headline")}</h1>

        <form onSubmit={submit} className="mt-10 grid grid-cols-1 gap-8 xl:grid-cols-[1fr_360px]">
          <div className="space-y-6">
            {/* Top row: Besteld door | Leveren aan | Factuur voor side-by-side on desktop */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Besteld door */}
              <Section title={t("checkout.section_ordered_by")}>
              <Input label={t("checkout.full_name")} value={form.customer_name} onChange={(v) => set("customer_name", v)} required testId="checkout-name" />
              <Input label={t("checkout.email_label")} type="email" value={form.customer_email} onChange={(v) => set("customer_email", v)} required testId="checkout-email" />
              <Input label={t("checkout.phone_optional")} value={form.customer_phone} onChange={(v) => set("customer_phone", v)} testId="checkout-phone" />
              <Input label={t("checkout.company_label")} value={form.company} onChange={(v) => set("company", v)} testId="checkout-company" />
              {country.reverse && (
                <Input
                  label={t("checkout.vat_number_required")}
                  value={form.vat_number}
                  onChange={(v) => set("vat_number", v)}
                  testId="checkout-vat-number"
                  placeholder={t("checkout.vat_number_placeholder")}
                />
              )}
            </Section>

            {/* Leveren aan */}
            <Section
              title={t("checkout.section_ship_to")}
              toggle={(
                <label className="flex cursor-pointer items-center gap-2 text-xs font-semibold text-zinc-600">
                  <input
                    type="checkbox"
                    checked={shipSameAsOrdered}
                    onChange={(e) => setShipSameAsOrdered(e.target.checked)}
                    className="h-4 w-4 accent-nibro-sage"
                    data-testid="checkout-ship-same-as-ordered"
                  />
                  {t("checkout.same_as_ordered_by")}
                </label>
              )}
            >
              {!shipSameAsOrdered && (
                <>
                  <Input label={t("checkout.contact_person")} value={form.shipping_contact_name} onChange={(v) => set("shipping_contact_name", v)} testId="checkout-ship-contact" placeholder={t("checkout.contact_person_placeholder")} />
                  <Input label={t("checkout.company_shipping")} value={form.shipping_company} onChange={(v) => set("shipping_company", v)} testId="checkout-ship-company" placeholder={t("checkout.company_shipping_placeholder")} />
                </>
              )}
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-zinc-600">{t("checkout.country")}</label>
                <select
                  value={form.shipping_country_code}
                  onChange={(e) => set("shipping_country_code", e.target.value)}
                  required
                  className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-nibro-sage focus:outline-none focus:ring-2 focus:ring-nibro-sage/30"
                  data-testid="checkout-country"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>
              <Input label={t("checkout.street_number")} value={form.shipping_address} onChange={(v) => set("shipping_address", v)} required testId="checkout-address" />
              <div className="grid grid-cols-2 gap-3">
                <Input label={t("checkout.postal_label")} value={form.shipping_postal} onChange={(v) => set("shipping_postal", v)} required testId="checkout-postal" />
                <Input label={t("checkout.city_label")} value={form.shipping_city} onChange={(v) => set("shipping_city", v)} required testId="checkout-city" />
              </div>
            </Section>

            {/* Factuur voor — moved here so the top 3 cards align side-by-side on desktop */}
            <Section
              title={t("checkout.section_bill_to")}
              toggle={(
                <label className="flex cursor-pointer items-center gap-2 text-xs font-semibold text-zinc-600">
                  <input
                    type="checkbox"
                    checked={billSameAsShip}
                    onChange={(e) => setBillSameAsShip(e.target.checked)}
                    className="h-4 w-4 accent-nibro-sage"
                    data-testid="checkout-bill-same-as-ship"
                  />
                  {t("checkout.same_as_ship_to")}
                </label>
              )}
            >
              {billSameAsShip ? (
                <div className="rounded border border-zinc-200 bg-zinc-50 p-3 text-[11px] text-zinc-500">
                  {t("checkout.billing_same_notice")}
                </div>
              ) : (
                <>
                  <Input label={t("checkout.billing_contact_person")} value={form.billing_contact_name} onChange={(v) => set("billing_contact_name", v)} testId="checkout-bill-contact" />
                  <Input label={t("checkout.billing_company")} value={form.billing_company} onChange={(v) => set("billing_company", v)} testId="checkout-bill-company" />
                  <Input label={t("checkout.billing_vat_number")} value={form.billing_vat_number} onChange={(v) => set("billing_vat_number", v)} testId="checkout-bill-vat-number" placeholder={t("checkout.billing_vat_number_placeholder")} />
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-zinc-600">{t("checkout.country")}</label>
                    <select
                      value={form.billing_country_code}
                      onChange={(e) => set("billing_country_code", e.target.value)}
                      required
                      className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-nibro-sage focus:outline-none focus:ring-2 focus:ring-nibro-sage/30"
                      data-testid="checkout-bill-country"
                    >
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <Input label={t("checkout.street_number")} value={form.billing_address} onChange={(v) => set("billing_address", v)} required testId="checkout-bill-address" />
                  <div className="grid grid-cols-2 gap-3">
                    <Input label={t("checkout.postal_label")} value={form.billing_postal} onChange={(v) => set("billing_postal", v)} required testId="checkout-bill-postal" />
                    <Input label={t("checkout.city_label")} value={form.billing_city} onChange={(v) => set("billing_city", v)} required testId="checkout-bill-city" />
                  </div>
                </>
              )}
            </Section>
            </div>
            {/* ↑ close 3-col side-by-side grid */}

            {/* Verzendmethode — live carrier rates from TFF Express */}
            <Section
              title={t("checkout.section_shipping_method")}
              toggle={
                <button
                  type="button"
                  onClick={tq.refresh}
                  disabled={tq.loading || !form.shipping_postal}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-nibro-sage-dark hover:underline disabled:text-zinc-400"
                  data-testid="transport-refresh"
                >
                  <RefreshCw size={12} className={tq.loading ? "animate-spin" : ""} />
                  {t("checkout.refresh_rates")}
                </button>
              }
            >
              {!form.shipping_postal || !form.shipping_country_code || form.shipping_country_code === "OTHER" ? (
                <div className="rounded border border-dashed border-zinc-300 p-4 text-center text-xs text-zinc-500">
                  {t("checkout.rates_no_postal")}
                </div>
              ) : tq.loading ? (
                <div className="flex items-center justify-center gap-3 rounded border border-zinc-200 bg-zinc-50 p-6 text-sm text-zinc-600">
                  <Loader2 size={16} className="animate-spin text-nibro-sage-dark" />
                  {t("checkout.rates_loading")}
                </div>
              ) : tq.error ? (
                <div className="space-y-2">
                  <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    {t("checkout.rates_error", { fallback: fallbackShipping.toFixed(2) })}
                  </div>
                  <div className="text-[10px] text-zinc-500">{tq.error}</div>
                </div>
              ) : tq.options.length === 0 ? (
                <div className="rounded border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-500">
                  {t("checkout.rates_empty")}
                </div>
              ) : (
                <div className="space-y-1.5" data-testid="transport-options">
                  {tq.options.map((opt, idx) => {
                    const active = tq.selected && tq.selected.carrier === opt.carrier && tq.selected.label === opt.label;
                    return (
                      <button
                        key={`${opt.carrier}-${opt.label}-${idx}`}
                        type="button"
                        onClick={() => tq.setSelected(opt)}
                        data-testid={`transport-opt-${idx}`}
                        className={`flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2.5 text-left transition ${
                          active
                            ? "border-nibro-sage-dark bg-nibro-sage-pale"
                            : "border-zinc-200 bg-white hover:border-nibro-sage hover:bg-zinc-50"
                        }`}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${active ? "border-nibro-sage-dark bg-white" : "border-zinc-300 bg-zinc-50"}`}>
                            {active ? <Check size={14} className="text-nibro-sage-dark" /> : <Truck size={14} className="text-zinc-500" />}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate text-sm font-bold text-zinc-900">{opt.carrier}</div>
                            <div className="truncate text-[11px] text-zinc-500">
                              {opt.label}
                              {opt.delivery_date?.label && ` · ${t("checkout.delivery_on")} ${opt.delivery_date.label}`}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-display text-base font-bold text-zinc-900">€{opt.price.toFixed(2)}</div>
                          {opt.badge && (
                            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{opt.badge}</div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                  {tq.meta?.fromCache && (
                    <div className="pt-1 text-[10px] text-zinc-400">
                      {t("checkout.rates_from_cache")}
                    </div>
                  )}
                </div>
              )}
            </Section>


            {/* Factuur voor — moved to top row, this slot left blank */}

            <Section title={t("checkout.section_notes")}>
              <textarea
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                rows={3}
                className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-nibro-sage focus:outline-none"
                placeholder={t("checkout.notes_placeholder")}
                data-testid="checkout-notes"
              />
            </Section>

            {/* Payment — factuur only */}
            <div className="rounded-md border-2 border-nibro-sage bg-nibro-sage-pale p-5" data-testid="checkout-payment-card">
              <div className="flex items-start gap-3">
                <FileText size={22} className="mt-0.5 flex-shrink-0 text-nibro-sage-dark" />
                <div>
                  <div className="font-display text-base font-bold text-zinc-900">{t("checkout.payment_invoice_title")}</div>
                  <div className="mt-1 text-sm leading-relaxed text-zinc-700">
                    {t("checkout.payment_invoice_body", { email: form.customer_email || t("checkout.payment_email_fallback") })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Order summary */}
          <div className="h-fit space-y-4 rounded-md border-2 border-nibro-sage bg-white p-6">
            <div className="label-uppercase">{t("checkout.order_summary")}</div>
            <div className="space-y-2 border-b border-zinc-100 pb-4">
              {items.map((i) => (
                <div key={i.lineId} className="flex items-center justify-between text-xs">
                  <span className="line-clamp-1 text-zinc-700">{i.quantity}× {i.name}</span>
                  <span className="ml-2 font-mono text-zinc-500">€{(i.quantity * i.unit_price).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <Row label={t("checkout.subtotal_ex_vat")} value={`€${subtotal.toFixed(2)}`} />
            {country.reverse ? (
              <div className="rounded border border-aramfix-red/30 bg-aramfix-red/5 px-3 py-2 text-[11px] leading-relaxed text-aramfix-red" data-testid="vat-reverse-notice">
                {t("checkout.vat_reverse_notice", { country: country.name })}
              </div>
            ) : (
              <Row label={t("checkout.vat_line", { pct: Math.round(country.vat * 100) })} value={`€${vat.toFixed(2)}`} />
            )}
            <Row label={shipping === 0 ? t("checkout.shipping_free") : t("checkout.shipping_label")} value={shipping === 0 ? "€0.00" : `€${shipping.toFixed(2)}`} />
            <div className="border-t border-zinc-100 pt-3">
              <div className="flex items-baseline justify-between">
                <span className="font-display font-bold">{t("checkout.total")}</span>
                <span className="font-display text-2xl font-bold text-nibro-sage-dark" data-testid="checkout-total">€{total.toFixed(2)}</span>
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-md bg-nibro-sage px-4 py-3 text-sm font-bold text-white transition hover:bg-nibro-sage-hover disabled:opacity-50"
              data-testid="checkout-submit"
            >
              {submitting ? <><Loader2 size={14} className="animate-spin" /> {t("checkout.placing_order")}</> : <><Check size={14} /> {t("checkout.place_order")}</>}
            </button>
            <p className="flex items-start gap-1.5 text-center text-[11px] text-zinc-500">
              <Info size={11} className="mt-0.5 flex-shrink-0" />
              <span className="text-left">{t("checkout.confirmation_note")}</span>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children, toggle }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="label-uppercase">{title}</div>
        {toggle}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Input({ label, value, onChange, type = "text", required, testId, placeholder }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-zinc-600">{label}</label>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        placeholder={placeholder}
        className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-nibro-sage focus:outline-none focus:ring-2 focus:ring-nibro-sage/30"
        data-testid={testId}
      />
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-zinc-600">{label}</span>
      <span className="font-mono text-zinc-900">{value}</span>
    </div>
  );
}
